import {
  Component,
  ChangeDetectionStrategy,
  input,
  output,
  signal,
  effect,
  computed,
  ViewEncapsulation,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { DropDownsModule } from '@progress/kendo-angular-dropdowns';
import { DatePickerModule } from '@progress/kendo-angular-dateinputs';
import { ButtonsModule } from '@progress/kendo-angular-buttons';
import { IconsModule } from '@progress/kendo-angular-icons';
import { filterIcon } from '@progress/kendo-svg-icons';
import { Observable, of } from 'rxjs';
import { TreeSelectModule } from 'primeng/treeselect';
import { TreeNode } from 'primeng/api';

export interface TimeRange {
  text: string;
  value: string;
}

export interface FilterConfig {
  showTimeRange?: boolean;
  showCustomerFilters?: boolean;
  showServiceFilters?: boolean;
  showToolFilters?: boolean;
  showCustomFilters?: boolean;
  showApplyButton?: boolean;
  showResetButton?: boolean;
}

export interface FilterState {
  timeRange?: TimeRange | null;
  startDate?: Date | null;
  endDate?: Date | null;
  selectedIndustries?: any[];
  selectedOrganizations?: any[];
  selectedServiceGroups?: string[];
  selectedTools?: any[]; // Changed from string[] to any[] to handle Product objects
  customFilters?: Record<string, any>;
}

@Component({
  selector: 'app-analytics-filter',
  templateUrl: './analytics-filter.component.html',
  styleUrls: ['./analytics-filter.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    DropDownsModule,
    DatePickerModule,
    ButtonsModule,
    IconsModule,
    TreeSelectModule,
  ],
})
export class AnalyticsFilterComponent {
  // Configuration inputs
  filterConfig = input<FilterConfig>({
    showTimeRange: true,
    showCustomerFilters: true,
    showServiceFilters: true,
    showToolFilters: true,
    showCustomFilters: false,
    showApplyButton: true,
    showResetButton: false,
  });

  // Data inputs
  timeRanges = input<TimeRange[]>([
    { text: 'Last 7 days', value: '7d' },
    { text: 'Last 30 days', value: '30d' },
    { text: 'Last 90 days', value: '90d' },
    { text: 'Custom', value: 'custom' },
  ]);
  selectedTimeRange = input<TimeRange | null>(null);
  startDate = input<Date | null>(null);
  endDate = input<Date | null>(null);
  industries = input<any[]>([]);
  selectedIndustries = input<any[]>([]);
  selectedOrganizations = input<any[]>([]);
  groupedServices = input<any[]>([]);
  selectedServiceGroups = input<string[]>([]);
  filteredProductsByService = input<any[]>([]);
  selectedTools = input<any[]>([]);
  customFilters = input<Record<string, any>>({});
  orgService = input<any>(null); // Data service for organization dropdown
  organizationData = input<any[]>([]); // Direct organization data input

  // Organization tree data - computed from the organization service (PrimeNG TreeNode format)
  protected readonly organizationTreeData = computed(() => {
    // Prefer direct organizationData input, fall back to loaded data
    const directData = this.organizationData();
    const loadedData = this.organizationsSignal();

    const organizations = directData.length > 0 ? directData : loadedData;

    if (!organizations || organizations.length === 0) {
      return [];
    }

    return this.transformToPrimeNGTreeData(organizations);
  });

  // Model for PrimeNG TreeSelect (uses TreeNode keys for selection)
  protected selectedOrganizationsModel: any = null;

  // Outputs
  onTimeRangeChange = output<TimeRange>();
  onStartDateChange = output<Date | null>();
  onEndDateChange = output<Date | null>();
  onIndustryChange = output<any[]>();
  onOrganizationsChange = output<any[]>();
  onServiceGroupChange = output<string[]>();
  onToolsChange = output<any[]>();
  onCustomFilterChange = output<Record<string, any>>();
  onApplyFilters = output<FilterState>();
  onResetFilters = output<void>();

  // Internal state
  protected readonly filterIcon = filterIcon;
  protected readonly currentFilterState = signal<FilterState>({});
  protected readonly organizationsSignal = signal<any[]>([]);
  protected readonly isLoadingOrganizations = signal<boolean>(false);
  private isLoadingOrgs = false;

  // Industry filter is a small, already-loaded list - filter client-side, no server round-trip
  protected readonly industrySearchTerm = signal('');
  protected readonly filteredIndustries = computed(() => {
    const term = this.industrySearchTerm().trim().toLowerCase();
    const all = this.industries();
    if (!term) return all;
    return all.filter((industry) => (industry.display_text || '').toLowerCase().includes(term));
  });

  constructor() {
    this.initEffect();
  }

  private initEffect(): void {
    // Watch for input changes and update internal state
    effect(
      () => {
        const state: FilterState = {
          timeRange: this.selectedTimeRange(),
          startDate: this.startDate(),
          endDate: this.endDate(),
          selectedIndustries: this.selectedIndustries(),
          selectedOrganizations: this.selectedOrganizations(),
          selectedServiceGroups: this.selectedServiceGroups(),
          selectedTools: this.selectedTools(),
          customFilters: this.customFilters(),
        };
        this.currentFilterState.set(state);

        // Sync selectedOrganizationsModel with input changes (e.g., reset)
        const selectedOrgs = this.selectedOrganizations();
        if (!selectedOrgs || selectedOrgs.length === 0) {
          // Clear the TreeSelect model when organizations are cleared
          this.selectedOrganizationsModel = null;
        }
      },
      { allowSignalWrites: true }
    ); // FIXED: Added allowSignalWrites option
  }

  // Event handlers
  protected onTimeRangeChangeHandler(timeRange: TimeRange): void {
    this.onTimeRangeChange.emit(timeRange);
  }

  protected onStartDateChangeHandler(date: Date | null): void {
    this.onStartDateChange.emit(date);
  }

  protected onEndDateChangeHandler(date: Date | null): void {
    this.onEndDateChange.emit(date);
  }

  protected onIndustryChangeHandler(industries: any[]): void {
    this.onIndustryChange.emit(industries);
  }

  protected onIndustryFilterChangeHandler(term: string): void {
    this.industrySearchTerm.set(term);
  }

  protected selectAllIndustriesHandler(): void {
    this.onIndustryChange.emit(this.industries());
  }

  protected clearIndustriesHandler(): void {
    this.onIndustryChange.emit([]);
  }

  /**
   * Handles organization selection changes from PrimeNG TreeSelect
   * Called when nodes are selected or unselected
   */
  protected onOrganizationNodeChange(): void {
    const model = this.selectedOrganizationsModel;

    if (!model) {
      this.onOrganizationsChange.emit([]);
      return;
    }

    // PrimeNG returns an array of TreeNode objects
    if (Array.isArray(model)) {
      // Extract organization IDs from TreeNode array
      const selectedIds = model
        .map((node) => (node.key ? parseInt(node.key) : null))
        .filter((id): id is number => id !== null);

      // Get the actual organization objects
      const selectedOrgs = this.findOrganizationsByIds(this.organizationsSignal(), selectedIds);
      this.onOrganizationsChange.emit(selectedOrgs);
      return;
    }

    // Fallback to object structure
    // Model is like: { '1': { checked: true, partialChecked: false }, '2': { ... } }
    const selectedIds = Object.keys(model || {})
      .filter((key) => {
        const node = model[key];
        // Include if checked and not partial (partial means only some children selected)
        return node?.checked === true && node?.partialChecked !== true;
      })
      .map((key) => parseInt(key));

    // Get the actual organization objects
    const selectedOrgs = this.findOrganizationsByIds(this.organizationsSignal(), selectedIds);
    this.onOrganizationsChange.emit(selectedOrgs);
  }

  /**
   * Gets the count of selected organizations
   */
  protected getSelectedOrganizationCount(): number {
    if (!this.selectedOrganizationsModel) {
      return 0;
    }

    // PrimeNG returns an array of TreeNode objects
    if (Array.isArray(this.selectedOrganizationsModel)) {
      return this.selectedOrganizationsModel.length;
    }

    // Fallback to object structure
    return Object.keys(this.selectedOrganizationsModel).filter((key) => {
      const node = this.selectedOrganizationsModel[key];
      return node?.checked === true && node?.partialChecked !== true;
    }).length;
  }

  /**
   * Gets the first selected node for chip display
   */
  protected getVisibleSelectedNodes(): TreeNode[] {
    const model = this.selectedOrganizationsModel;
    if (!model) {
      return [];
    }

    // PrimeNG returns an array of TreeNode objects
    if (Array.isArray(model)) {
      return model.slice(0, 1);
    }

    // Fallback to object structure
    const selectedNodes: TreeNode[] = [];
    const allTreeData = this.organizationTreeData();

    // Recursively find selected nodes from tree data
    const findSelectedNodes = (nodes: TreeNode[]) => {
      for (const node of nodes) {
        if (node.key && model[node.key]) {
          const nodeData = model[node.key];
          if (nodeData.checked === true && nodeData.partialChecked !== true) {
            selectedNodes.push(node);
            if (selectedNodes.length >= 1) {
              return;
            }
          }
        }
        if (node.children && selectedNodes.length < 1) {
          findSelectedNodes(node.children);
        }
      }
    };

    findSelectedNodes(allTreeData);
    return selectedNodes;
  }

  /**
   * Removes a node from selection
   */
  protected removeNode(event: Event, node: TreeNode): void {
    event.stopPropagation();

    if (this.selectedOrganizationsModel && node.key) {
      // PrimeNG uses array of TreeNode objects
      if (Array.isArray(this.selectedOrganizationsModel)) {
        // Filter out the node with matching key
        this.selectedOrganizationsModel = this.selectedOrganizationsModel.filter((n) => n.key !== node.key);
      } else {
        // Fallback to object structure
        delete this.selectedOrganizationsModel[node.key];
        this.selectedOrganizationsModel = { ...this.selectedOrganizationsModel };
      }

      // Emit the change
      this.onOrganizationNodeChange();
    }
  }

  /**
   * Recursively finds organizations by their IDs
   */
  private findOrganizationsByIds(organizations: any[], ids: number[]): any[] {
    const result: any[] = [];

    const findInTree = (orgs: any[]) => {
      for (const org of orgs) {
        if (ids.includes(org.id)) {
          result.push(org);
        }
        // Recursively check children
        if (org.children && org.children.length > 0) {
          findInTree(org.children);
        }
      }
    };

    findInTree(organizations);
    return result;
  }

  protected onOrganizationDropdownOpened(event: any): void {
    // Don't reload if already loading
    if (this.isLoadingOrgs || this.isLoadingOrganizations()) {
      return;
    }

    // Safety check: ensure orgService and state are available
    const service = this.orgService();
    if (!service || !service.state) {
      console.warn('Analytics filter: Cannot open dropdown - orgService or state not available');
      return;
    }

    // Reset service state like the original component
    service.state.skip = 0;
    service.state.take = 100; // Match the pattern of other dropdowns

    // Ensure we have a proper filter
    if (!service.state.filter) {
      service.state.filter = {
        logic: 'and',
        filters: [],
      };
    }

    // Load data
    this.loadOrganizationData();
  }

  protected onServiceGroupChangeHandler(serviceGroups: string[]): void {
    this.onServiceGroupChange.emit(serviceGroups);
  }

  protected selectAllServiceGroupsHandler(): void {
    this.onServiceGroupChange.emit(this.groupedServices().map((service) => service.group));
  }

  protected clearServiceGroupsHandler(): void {
    this.onServiceGroupChange.emit([]);
  }

  protected onToolsChangeHandler(tools: any[]): void {
    this.onToolsChange.emit(tools);
  }

  protected onCustomFilterChangeHandler(key: string, value: any): void {
    const currentFilters = this.currentFilterState().customFilters || {};
    const updatedFilters = { ...currentFilters, [key]: value };
    this.onCustomFilterChange.emit(updatedFilters);
  }

  protected onApplyFiltersHandler(): void {
    this.onApplyFilters.emit(this.currentFilterState());
  }

  protected onResetFiltersHandler(): void {
    this.onResetFilters.emit();
  }

  // Utility methods
  protected hasActiveFilters(): boolean {
    const state = this.currentFilterState();
    return !!(
      state.timeRange ||
      state.startDate ||
      state.endDate ||
      (state.selectedIndustries && state.selectedIndustries.length > 0) ||
      (state.selectedOrganizations && state.selectedOrganizations.length > 0) ||
      (state.selectedServiceGroups && state.selectedServiceGroups.length > 0) ||
      (state.selectedTools && state.selectedTools.length > 0) ||
      Object.keys(state.customFilters || {}).length > 0
    );
  }

  protected getFilterSummary(): string {
    const state = this.currentFilterState();
    const filters: string[] = [];

    if (state.timeRange) filters.push(`Time: ${state.timeRange.text}`);
    if (state.selectedIndustries && state.selectedIndustries.length > 0) {
      filters.push(`Industry: ${state.selectedIndustries.map((i) => i.display_text).join(', ')}`);
    }
    if (state.selectedServiceGroups && state.selectedServiceGroups.length > 0) {
      filters.push(`Service: ${state.selectedServiceGroups.join(', ')}`);
    }
    if (state.selectedTools && state.selectedTools.length > 0) {
      filters.push(`Tools: ${state.selectedTools.length} selected`);
    }

    return filters.length > 0 ? filters.join(', ') : 'No filters applied';
  }

  // Load organization data from the service
  private loadOrganizationData(): void {
    const service = this.orgService();
    if (!service || !service.state) {
      console.warn('Analytics filter: Cannot get organization data - service or state not available');
      return;
    }

    // Guard against duplicate loads
    if (this.isLoadingOrgs) {
      return;
    }

    this.isLoadingOrgs = true;
    this.isLoadingOrganizations.set(true);

    // Ensure state properties are set safely
    service.state.skip = 0;
    service.state.take = 2000; // Load more organizations for tree view

    if (!service.state.filter) {
      service.state.filter = { logic: 'and', filters: [] };
    }

    // Load data from service with error handling
    try {
      service.read((response: any) => {
        this.isLoadingOrgs = false;
        this.isLoadingOrganizations.set(false);

        if (response && response.data) {
          this.organizationsSignal.set(response.data);
        } else if (response && response.value) {
          this.organizationsSignal.set(response.value);
        } else {
          console.warn('Analytics filter: Unexpected response format:', response);
        }
      });
    } catch (error) {
      console.error('Error loading organization data:', error);
      this.isLoadingOrgs = false;
      this.isLoadingOrganizations.set(false);
    }
  }

  // Transform flat organization data to PrimeNG TreeNode hierarchical structure
  private transformToPrimeNGTreeData(organizations: any[]): TreeNode[] {
    if (!organizations || organizations.length === 0) {
      return [];
    }

    // Create a map for quick lookup of all organizations in our dataset
    const orgMap = new Map<number, TreeNode>();
    const rootOrgs: TreeNode[] = [];

    // First pass: create all TreeNodes
    organizations.forEach((org) => {
      const treeNode: TreeNode = {
        key: org.id.toString(),
        label: org.name,
        data: org, // Store full organization object
        children: [],
        selectable: true,
        leaf: false,
      };
      orgMap.set(org.id, treeNode);
    });

    // Second pass: build the hierarchy
    organizations.forEach((org) => {
      const node = orgMap.get(org.id);
      if (!node) return;

      if (org.parent && org.parent.id) {
        // Check for self-referential parent (dirty data protection)
        if (org.parent.id === org.id) {
          // Self-referential parent - treat as root
          rootOrgs.push(node);
        } else {
          // Check if the parent exists in our current dataset
          const parentNode = orgMap.get(org.parent.id);

          if (parentNode) {
            // Parent exists in dataset - this is a child of another organization
            if (!parentNode.children) {
              parentNode.children = [];
            }
            parentNode.children.push(node);
          } else {
            // Parent not in dataset - this is a direct descendant of the root org
            rootOrgs.push(node);
          }
        }
      } else {
        // No parent - this is a true root
        rootOrgs.push(node);
      }
    });

    // Mark leaf nodes (nodes with no children)
    this.markLeafNodes(rootOrgs);

    return rootOrgs;
  }

  /**
   * Recursively marks leaf nodes in the tree
   */
  private markLeafNodes(nodes: TreeNode[]): void {
    for (const node of nodes) {
      if (!node.children || node.children.length === 0) {
        node.leaf = true;
        node.children = undefined; // Remove empty children array
      } else {
        node.leaf = false;
        this.markLeafNodes(node.children);
      }
    }
  }
}
